// src/javascriptComponents/skillsScreen.js
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { ThreeDots } from 'react-loader-spinner';
import '../css/skillsScreen.css';
import updateTokenUsage from './updateTokenUsage';
import { AI_FUNCTIONS_BASE_URL } from '../config/api';
import { buildTransferableSkillsLexicon } from './transferableSkillsLexicon';
import {
  buildSkillContextFromForm,
  optimizeSkillsForResume,
  randomizeSkills,
  sortSkillsByRelevance,
} from './skillCuration';

// --- helpers ---
const clean = (v) =>
  String(v ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const uniqCI = (arr) => {
  const seen = new Set();
  const out = [];
  for (const s of arr || []) {
    const t = clean(s);
    const k = t.toLowerCase();
    if (!t || seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
};

// collect profile context: majors/minors, courses, work bullets
export default function SkillsScreen({
  formData,
  setFormData,
  setTokenUsage,
  setUsagePercentage,
  tokenThreshold,
}) {
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');
  const [manualSkill, setManualSkill] = useState('');

  // Display context for the user
  const resumeLexicon = useMemo(
    () => buildTransferableSkillsLexicon(formData),
    [formData],
  );
  const skillContext = useMemo(
    () => buildSkillContextFromForm(formData, { signals: resumeLexicon.signals }),
    [formData, resumeLexicon.signals],
  );

  const handleSelectSkill = (skill) => {
    const s = clean(skill);
    if (!s) return;
    const currentSkills = uniqCI(formData.skills || []);
    const currentSelected = uniqCI(formData.selectedSkills || []);
    const isSelected = currentSelected.map(x=>x.toLowerCase()).includes(s.toLowerCase());

    const nextSelected = isSelected
      ? currentSelected.filter((x) => x.toLowerCase() !== s.toLowerCase())
      : uniqCI([...currentSelected, s]);

    const nextSkills = isSelected
      ? currentSkills.filter((x) => x.toLowerCase() !== s.toLowerCase())
      : uniqCI([...currentSkills, s]);

    setFormData({ ...formData, selectedSkills: nextSelected, skills: nextSkills });
  };

  const addManualSkill = () => {
    const s = clean(manualSkill);
    if (!s) return;
    const nextSkills = uniqCI([...(formData.skills || []), s]);
    const nextSelected = uniqCI([...(formData.selectedSkills || []), s]);
    setFormData({ ...formData, skills: nextSkills, selectedSkills: nextSelected });
    setManualSkill('');
  };

  const selectAll = () => {
    const chosen = uniqCI([...recommendations]);
    const nextSkills = uniqCI([...(formData.skills || []), ...chosen]);
    const nextSelected = uniqCI([...(formData.selectedSkills || []), ...chosen]);
    setFormData({ ...formData, skills: nextSkills, selectedSkills: nextSelected });
  };

  const clearAllRecommendedFromSelection = () => {
    const recSet = new Set(recommendations.map((r) => r.toLowerCase()));
    const nextSelected = (formData.selectedSkills || []).filter((s) => !recSet.has(s.toLowerCase()));
    const nextSkills = (formData.skills || []).filter((s) => !recSet.has(s.toLowerCase()));
    setFormData({ ...formData, selectedSkills: nextSelected, skills: uniqCI(nextSkills) });
  };

  const optimizeCurrentSkills = () => {
    const sourceSkills = (formData.selectedSkills || []).length
      ? formData.selectedSkills
      : formData.skills;
    const optimized = optimizeSkillsForResume(sourceSkills, skillContext, formData);
    setFormData({ ...formData, skills: optimized, selectedSkills: optimized });
  };

  const randomizeCurrentSkills = () => {
    const sourceSkills = (formData.selectedSkills || []).length
      ? formData.selectedSkills
      : formData.skills;
    const shuffled = randomizeSkills(sourceSkills);
    setFormData({ ...formData, skills: shuffled, selectedSkills: shuffled });
  };

  const getRecommendations = async () => {
    setIsLoading(true);
    setErr('');
    try {
      // Build richer payload but keep original contract (prompt: "getSkills")
      const payload = {
        prompt: 'getSkills',
        userID: formData.user,
        // original field the backend used:
        courses: resumeLexicon.academics.join(', '),
        signals: resumeLexicon.signals,
        resumeLexicon,
      };

      const response = await axios.post(
        `${AI_FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      // backend used to return comma-separated string
      const suggested = Array.isArray(response.data)
        ? response.data
        : typeof response.data === 'string'
          ? response.data.split(',').map((s) => clean(s))
          : Array.isArray(response.data?.skills)
            ? response.data.skills.map(clean)
            : [];

      // filter out already-present skills
      const already = new Set((formData.skills || []).map((s) => s.toLowerCase()));
      const filtered = sortSkillsByRelevance(
        uniqCI(suggested.filter((s) => !already.has(s.toLowerCase()))),
        skillContext,
        formData,
      );

      setRecommendations(filtered);

      // token usage bump (same behavior you had)
      await updateTokenUsage(formData.user, tokenThreshold, setTokenUsage, setUsagePercentage);
    } catch (e) {
      if (e?.response?.status === 403) {
        setErr('Token limit exceeded. Please contact support for additional tokens.');
      } else {
        setErr('Failed to fetch recommendations. Please try again later.');
      }
      setRecommendations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Short-circuit UI when we truly have no course signals
  if (!resumeLexicon.hasExperienceSignals) {
    return <p>Add some experience or credentials in the Form first.</p>;
  }

  return (
    <div className="skills-screen">
      <h1>Skill Suggestions</h1>

      <div className="skills-screen responsive-box">
        {/* Academics / signals preview */}
        <div className="skills-screen subjects-learned">
 


        </div>


        {/* Action to fetch recommendations */}
        <button
          onClick={getRecommendations}
          className="skills-screen recommendation-button"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
              <ThreeDots height="14" width="36" color="#ffffff" ariaLabel="loading" visible />
              Fetching skills…
            </span>
          ) : (
            'Get Skills Recommendations'
          )}
        </button>
        {err && <p className="skills-screen error-message">{err}</p>}

        {/* Recommendations as chips */}
        {recommendations.length > 0 && (
          <div className="recommendations">
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: 0 }}>Recommended Skills</h2>
          </div>

          <ul className="recommendations-list">
            {recommendations.map((skill, index) => {
              const isSelected =
                (formData.selectedSkills || []).map((x) => x.toLowerCase()).includes(skill.toLowerCase());
              return (
              <li key={index} className="recommendation-item">
                <button
                    className={`recommendedItem ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectSkill(skill)}
                    title={skill}
                  >
                    {skill}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="skills-screen recommendations-actions">
            <button onClick={selectAll}>Select All</button>
            <button onClick={clearAllRecommendedFromSelection}>Clear</button>
          </div>
          </div>
        )}

        {/* Manual add */}
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          <h3 style={{ margin: 0 }}>Add a Skill Manually</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
            <input
              type="text"
              value={manualSkill}
              onChange={(e) => setManualSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addManualSkill();
              }}
              placeholder="e.g., Python, Classroom Management"
              style={{ width: '100%', border: '1px solid #ccc', borderRadius: 6, padding: '6px 8px' }}
            />
            <button onClick={addManualSkill}>Add</button>
          </div>
        </div>

        {/* Current snapshot */}
        <div style={{ marginTop: 12 }}>
          <h3 style={{ marginBottom: 6 }}>Your Current Skills (pipe-separated)</h3>
          <div className="skills-screen recommendations-actions" style={{ marginBottom: 8 }}>
            <button type="button" onClick={optimizeCurrentSkills}>
              Optimize Skills
            </button>
            <button type="button" onClick={randomizeCurrentSkills}>
              Randomize Skills
            </button>
          </div>
          <div className="pipe-separated-list">
            {uniqCI(formData.skills || []).join(' | ') || '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

